import { IComboBoxOption, Stack, Text } from '@fluentui/react';
import { Button, buttonStylesLink, buttonStylesStealth, Checkbox, ComboBox } from '@h2oai/ui-kit';
import { memo, useCallback, useMemo } from 'react';

import { PageFilters } from '../../../../components/PageFilters/PageFilters';
import { PageFiltersElement } from '../../../../components/PageFilters/types';
import { AuditTrailPageControlsProps } from '../../types';
import { auditTrailFiltersConfig } from './auditTrailFiltersConfig';

export const AuditTrailPageControls = memo((props: AuditTrailPageControlsProps) => {
  const {
    columns,
    selectedColumns,
    onColumnsChange,
    onFiltersChange,
    onClearFilters,
    currentFilters,
    allWorkspaces,
    toggleAllWorkspaces,
    toggleAllWorkspacesDisabled,
    showReadOnlyEvents,
    toggleShowReadOnlyEvents,
    onRefresh,
    isLoading = false,
    onDownloadJson,
    hasContent = false,
  } = props;

  const handleColumnsChange = (_e: unknown, option?: IComboBoxOption): void => {
    if (!option) return;

    const updatedColumns = option.selected
      ? [...selectedColumns, option.key as string]
      : selectedColumns.filter((col) => col !== option.key);

    onColumnsChange(updatedColumns);
  };

  // Create filters config with current values from URL
  const filtersWithCurrentValues: PageFiltersElement<any>[] = useMemo(() => {
    return auditTrailFiltersConfig.map((filter) => {
      const currentFilterState = currentFilters?.[filter.key];
      const disabled = filter.key === 'audit-trail-workspace-filter' && !allWorkspaces;

      return {
        ...filter,
        disabled: filter.disabled || disabled,
        state: {
          value: currentFilterState?.value ?? filter?.state?.value,
          readableValue: currentFilterState?.readableValue ?? filter?.state?.readableValue,
        },
      };
    });
  }, [currentFilters, allWorkspaces]);

  const handleClearFilters = useCallback(() => {
    onClearFilters?.();
  }, [onClearFilters]);

  const handleClearFilter = useCallback(
    (filterKey: string) => {
      if (onFiltersChange && currentFilters) {
        const newFilters = { ...currentFilters };
        delete newFilters[filterKey];
        onFiltersChange(newFilters);
      }
    },
    [onFiltersChange, currentFilters]
  );

  const handleRefresh = useCallback(() => {
    onRefresh?.();
  }, [onRefresh]);

  const handleDownloadJson = useCallback(() => {
    if (onDownloadJson) onDownloadJson();
  }, [onDownloadJson]);

  return (
    <Stack>
      <PageFilters
        filters={filtersWithCurrentValues}
        filterValues={currentFilters || {}}
        onFiltersChange={onFiltersChange}
        onClearFilters={handleClearFilters}
        onClearFilter={handleClearFilter}
      />

      <Stack
        horizontal
        verticalAlign="end"
        horizontalAlign="space-between"
        styles={{ root: { marginBottom: 16 } }}
        tokens={{ childrenGap: 8 }}
      >
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Button
            tooltip={toggleAllWorkspacesDisabled ? 'Searching across all workspaces is not allowed.' : undefined}
            onClick={toggleAllWorkspacesDisabled ? undefined : toggleAllWorkspaces}
            disabled={toggleAllWorkspacesDisabled}
          >
            <Checkbox
              id="toggle-all-workspaces"
              key="toggle-all-workspaces"
              styles={{ root: { pointerEvents: 'none' } }}
              checked={allWorkspaces}
            />
            Search within all Workspaces
          </Button>
          <Button onClick={toggleShowReadOnlyEvents}>
            <Checkbox
              id="toggle-read-only-events"
              key="toggle-read-only-events"
              styles={{ root: { pointerEvents: 'none' } }}
              checked={showReadOnlyEvents}
            />
            Include Read-Only Events
          </Button>
          <Button
            text="Refresh"
            iconName="Refresh"
            styles={[buttonStylesLink, { root: { marginLeft: 8 } }]}
            onClick={handleRefresh}
            disabled={isLoading}
          />
        </Stack>

        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          {hasContent && (
            <Button text="Download" iconName="DrillDown" styles={buttonStylesStealth} onClick={handleDownloadJson} />
          )}

          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Text>Columns:</Text>
            <ComboBox
              multiSelect
              options={columns}
              selectedKey={selectedColumns}
              onChange={handleColumnsChange}
              placeholder="Select columns to display"
              styles={{ root: { width: 300 } }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
});
